import { useState, useMemo } from 'react';
import { Game, getStatusLabel, isTerminal } from '@swtorbit/shared-socket-events';
import { ConfirmModal } from '../ui/ConfirmModal';
import { useGameContext } from '../contexts/GameContext';
import { formatDate } from '../utils/formatDate';

interface GamesListViewProps {
  onSelectGame: (game: Game) => void;
}

type StatusFilter = 'all' | 'ongoing' | 'finished';
type SortBy = 'newest' | 'oldest' | 'name';

/** Status text, including why a game was abandoned when the sweep did it. */
function statusText(game: Game): string {
  if (game.status === 'abandoned' && game.endReason === 'inactive') {
    return 'Abandoned (inactive 30 days)';
  }
  return getStatusLabel(game.status).label;
}

export function GamesListView({ onSelectGame }: GamesListViewProps) {
  const { games, selectedGame, deleteGame } = useGameContext();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [sortBy, setSortBy] = useState<SortBy>('newest');
  const [pendingDelete, setPendingDelete] = useState<Game | null>(null);

  // Room codes are reused once released, so several games can share one.
  const gamesPerRoomCode = useMemo(() => {
    const counts = new Map<string, number>();
    for (const game of games) counts.set(game.roomCode, (counts.get(game.roomCode) ?? 0) + 1);
    return counts;
  }, [games]);

  const filteredAndSortedGames = useMemo(() => {
    let result = [...games];

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(game =>
        game.roomCode.toLowerCase().includes(query) ||
        (game.name && game.name.toLowerCase().includes(query))
      );
    }

    if (statusFilter !== 'all') {
      const wantFinished = statusFilter === 'finished';
      result = result.filter(game => isTerminal(game.gameType, game.status) === wantFinished);
    }

    result.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return b.createdAt - a.createdAt;
        case 'oldest':
          return a.createdAt - b.createdAt;
        case 'name':
          return (a.name || '').localeCompare(b.name || '');
        default:
          return 0;
      }
    });

    return result;
  }, [games, searchQuery, statusFilter, sortBy]);

  const handleConfirmDelete = () => {
    if (!pendingDelete) return;
    deleteGame({ gameId: pendingDelete.dbId });
    setPendingDelete(null);
  };

  return (
    <div className="p-lg">
      <div className="d-flex gap-lg mb-lg flex-wrap align-items-center">
        <input
          type="text"
          className="input input--sm"
          placeholder="Search by room code or name..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{ maxWidth: '260px' }}
        />

        <div className="d-flex align-items-center gap-xs">
          <label className="text-white fs-sm">Status:</label>
          <select
            className="input input--sm"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
          >
            <option value="all">All</option>
            <option value="ongoing">Ongoing</option>
            <option value="finished">Finished</option>
          </select>
        </div>

        <div className="d-flex align-items-center gap-xs">
          <label className="text-white fs-sm">Sort:</label>
          <select
            className="input input--sm"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortBy)}
          >
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
            <option value="name">Name (A-Z)</option>
          </select>
        </div>
      </div>

      <div className="table-wrap">
        <table className="table table--hover">
          <thead>
            <tr>
              <th>Status</th>
              <th>Room Code</th>
              <th>Name</th>
              <th className="table__cell--num">Players</th>
              <th className="table__cell--num">Rounds</th>
              <th>Played</th>
              <th>Created</th>
              <th className="table__cell--actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredAndSortedGames.length === 0 ? (
              <tr>
                <td colSpan={8} className="table__empty">
                  {games.length === 0
                    ? 'No games yet. Create a game to get started!'
                    : 'No games match your filters.'}
                </td>
              </tr>
            ) : (
              filteredAndSortedGames.map((game) => {
                const isSelected = selectedGame?.dbId === game.dbId;
                const status = getStatusLabel(game.status);
                const codeShared = (gamesPerRoomCode.get(game.roomCode) ?? 0) > 1;
                return (
                  <tr key={game.dbId} className={isSelected ? 'is-selected' : undefined}>
                    <td>
                      <span className="d-inline-flex align-items-center gap-2xs">
                        <span aria-hidden="true">{status.icon}</span>
                        <span className="fs-sm">{statusText(game)}</span>
                      </span>
                    </td>
                    <td>
                      <span className="d-inline-flex align-items-center gap-xs">
                        <span className="badge badge--lg">{game.roomCode}</span>
                        {codeShared ? (
                          <span
                            className="badge badge--sm badge--amber"
                            title="More than one game in this list has used this room code. Check the dates."
                          >
                            reused
                          </span>
                        ) : game.roomCodeReleased ? (
                          <span
                            className="badge badge--sm"
                            title="This game no longer holds its room code; a future game may be given it."
                          >
                            released
                          </span>
                        ) : null}
                      </span>
                    </td>
                    <td className="text-truncate">{game.name || '—'}</td>
                    <td className="table__cell--num">{game.players.length}</td>
                    <td className="table__cell--num">{game.rounds.length}</td>
                    <td className="table__cell--muted">
                      {game.startedAt ? (
                        <div className="d-flex flex-column">
                          <span>{formatDate(game.startedAt)}</span>
                          {game.endedAt && <span className="fs-sm">to {formatDate(game.endedAt)}</span>}
                        </div>
                      ) : game.endedAt ? (
                        <span className="fs-sm">Ended {formatDate(game.endedAt)}</span>
                      ) : (
                        'Not started'
                      )}
                    </td>
                    <td className="table__cell--muted">{formatDate(game.createdAt)}</td>
                    <td className="table__cell--actions">
                      <div className="d-inline-flex gap-xs">
                        <button
                          className={`btn btn--sm ${isSelected ? 'btn--outline btn--slate' : ''}`}
                          onClick={() => onSelectGame(game)}
                        >
                          {isSelected ? 'Selected' : 'Select'}
                        </button>
                        <button
                          className="btn btn--sm btn--red"
                          onClick={() => setPendingDelete(game)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="d-flex justify-content-between align-items-center mt-sm pt-sm border-top">
        <span className="fs-sm text-white">
          Showing {filteredAndSortedGames.length} of {games.length} games
        </span>
      </div>

      <ConfirmModal
        isOpen={!!pendingDelete}
        onConfirm={handleConfirmDelete}
        onCancel={() => setPendingDelete(null)}
        title="Delete Game"
        message={
          <div className="d-flex flex-column gap-xs">
            <span>
              Permanently delete game <strong>{pendingDelete?.roomCode}</strong>
              {pendingDelete?.name ? ` (${pendingDelete.name})` : ''}
              {pendingDelete ? `, created ${formatDate(pendingDelete.createdAt)}` : ''}?
            </span>
            <span className="text-red">
              This will also delete all rounds, players and registrations for this game. This cannot be undone.
            </span>
          </div>
        }
        confirmText="Delete Game"
        variant="danger"
      />
    </div>
  );
}
